import { lazy, Suspense, type ReactNode } from 'react'
import { FullPageMessage } from '@/components/FullPageMessage'
import { Button } from '@/components/ui/button'
import { getConfig, type EntraConfig } from '@/lib/config'
import { DevAuthProvider } from './DevAuthProvider'

type EntraProviderProps = { config: EntraConfig; children: ReactNode }

// MSAL is large, and dev-auth deployments never use it, so it loads on demand.
const EntraAuthProvider = lazy(() =>
  import('./EntraAuthProvider').then(
    (m) => ({ default: m.EntraAuthProvider }),
    (err: unknown) => ({
      default: function SignInUnavailable(_: EntraProviderProps) {
        return (
          <FullPageMessage
            title="Sign-in couldn’t load"
            description={err instanceof Error ? err.message : String(err)}
            action={<Button onClick={() => window.location.reload()}>Reload</Button>}
          />
        )
      },
    }),
  ),
)

/** Chooses the auth implementation from runtime config (authMode). */
export function AuthProvider({ children }: { children: ReactNode }) {
  const config = getConfig()
  return config.authMode === 'entra' ? (
    <Suspense fallback={<FullPageMessage title="Signing you in" busy />}>
      <EntraAuthProvider config={config.entra}>{children}</EntraAuthProvider>
    </Suspense>
  ) : (
    <DevAuthProvider>{children}</DevAuthProvider>
  )
}
